/* eslint-disable react-hooks/exhaustive-deps */
import classNames from 'clsx';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { CasinoGameHeader, CasinoGameList, useWidgetAnchor } from '@swf/casino';
import LoadingIndicator from '@swf/components/LoadingIndicator/LoadingIndicator';
import NoGamesFound from '@swf/components/NoGamesFound/NoGamesFound';
import { CacheControl, Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import { Header } from '@swf/core/enums';
import usePageBottom from '@swf/core/hooks/usePageBottom';
import type { RootState } from '@swf/core/store';
import type { ShowField } from '@swf/core/types/CasinoGameList';
import type { WidgetFC } from '@swf/core/types/Widget';
import { getStyle } from '@swf/core/utils/casino';

import styles from './CasinoGameGridWidget.scss';
import { gridWidgetModel, useGridWidgetModel } from './model';

const CasinoGameGridWidget: WidgetFC = () => {
  const { widgetDispatch, widgetSelect, widgetConfig } = useGridWidgetModel();
  const { loading, games, complete, visibleByAuth } = useSelector(widgetSelect.get);
  const {
    display_title: displayTitle,
    sub_title: subTitle,
    layout,
    params,
    render_info: renderInfo,
    overlay_hierarchy: overlayHierarchy
  } = widgetConfig;
  const icon = useSelector((state: RootState) => widgetSelect.getIcon(state, widgetConfig));

  const shouldRender = visibleByAuth;
  const containerRef = useRef<HTMLDivElement>(null);
  const isPageBottom = usePageBottom(containerRef);
  const widgetTargetId = useWidgetAnchor();

  useEffect(() => {
    if (isPageBottom && !loading && !complete && games.length > 0) widgetDispatch.loadMore();
  }, [isPageBottom, loading]);

  return shouldRender ? (
    <section
      className={classNames(styles.container, styles[`${icon}Container` as keyof typeof styles])}
      style={getStyle(layout)}
      ref={containerRef}
      id={widgetTargetId}
    >
      <CacheControl>public, max-age=600</CacheControl>
      <Vary by={[Header.Currency]} />
      {displayTitle && (
        <CasinoGameHeader moreLink="" icon={icon} title={displayTitle} subTitle={subTitle} />
      )}
      <LoadingIndicator
        loading={loading && !games.length}
        variant="light"
        className={styles.spinner}
      >
        {!loading && !games.length ? (
          <NoGamesFound />
        ) : (
          <CasinoGameList
            params={params}
            games={games}
            icon={icon}
            renderInfo={renderInfo as ShowField[]}
            loading={loading && !complete}
            overlayHierarchy={overlayHierarchy}
          />
        )}
      </LoadingIndicator>
    </section>
  ) : null;
};

CasinoGameGridWidget.model = gridWidgetModel;

export default CasinoGameGridWidget;
